import { useState } from "react";
import { Button } from "@/components/ui/button";
import { signInWithGoogle } from "@/lib/firebase";
import { ENVIRONMENT } from "@/config";

const ENV_COLORS: Record<string, string> = {
  DEVELOPMENT: "bg-blue-100 text-blue-800",
  TEST: "bg-yellow-100 text-yellow-800",
  PRODUCTION: "bg-red-100 text-red-800",
};

function GoogleGIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.418 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.397 0-9.937-3.066-11.275-7.946l-6.522 5.025C9.504 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.084 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

export function Login() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to sign in.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-[0.2em] mb-6">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Asepha Redi
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                ENV_COLORS[ENVIRONMENT] || ENV_COLORS.DEVELOPMENT
              }`}
            >
              {ENVIRONMENT}
            </span>
          </div>
          <h1 className="text-5xl font-semibold text-foreground leading-[1.05] tracking-tight">
            Redi
            <br />
            <em className="not-italic text-muted-foreground">Dashboard</em>
          </h1>
          <p className="mt-5 text-muted-foreground text-[15px] leading-relaxed">
            Daily intake, retry console, patient registry,
            <br />
            and call records — all in one place.
          </p>
        </div>

        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <Button
            onClick={handleLogin}
            disabled={loading}
            variant="outline"
            className="w-full justify-center py-6 text-[15px] gap-2"
          >
            <GoogleGIcon size={18} />
            {loading ? "Signing in…" : "Continue with Google"}
          </Button>

          {error && (
            <div className="mt-4 px-3 py-2 rounded-md bg-red-50 text-red-700 text-xs border border-red-100">
              {error}
            </div>
          )}

          <div className="mt-6 pt-5 border-t text-xs text-muted-foreground">
            Sign in with your <span className="font-medium">@asepha.ai</span> or{" "}
            <span className="font-medium">@carelinkos.com</span> Google Workspace account.
          </div>
        </div>
      </div>
    </div>
  );
}
